import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GAP = 12;
const clamp = (value, low, high) => Math.max(low, Math.min(value, Math.max(low, high)));
const viewport = () => ({
  x: window.visualViewport?.offsetLeft || 0,
  y: window.visualViewport?.offsetTop || 0,
  width: window.visualViewport?.width || window.innerWidth,
  height: window.visualViewport?.height || window.innerHeight,
});

export function boundLauncher(position, size, view) {
  return {
    x: clamp(position.x, view.x + GAP, view.x + view.width - size.width - GAP),
    y: clamp(position.y, view.y + GAP, view.y + view.height - size.height - GAP),
  };
}

export function assistantPanelBounds(anchor, size, view) {
  const width = Math.min(500, Math.max(0, view.width - GAP * 2));
  const height = Math.min(700, Math.max(0, view.height - GAP * 2));
  const right = anchor.x + size.width + GAP;
  const left = anchor.x - width - GAP;
  const x = right + width <= view.x + view.width - GAP ? right
    : left >= view.x + GAP ? left : anchor.x;
  return {
    left: clamp(x, view.x + GAP, view.x + view.width - width - GAP),
    top: clamp(anchor.y + size.height - height, view.y + GAP, view.y + view.height - height - GAP),
    width, height,
  };
}

export default function FloatingAssistant({ open, onOpen, onClose, mark, title, residentName, children }) {
  const [view, setView] = useState(viewport);
  const [position, setPosition] = useState(null);
  const [panelPosition, setPanelPosition] = useState(null);
  const [size, setSize] = useState({ width: 210, height: 64 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const gesture = useRef(null);
  const panelGesture = useRef(null);
  const suppressClick = useRef(false);
  const wasOpen = useRef(false);
  const anchor = boundLauncher(position || { x: view.x + view.width - size.width - 24, y: view.y + view.height - size.height - 24 }, size, view);
  const bounds = assistantPanelBounds(anchor, size, view);
  if (panelPosition) {
    const bounded = boundLauncher(panelPosition, bounds, view);
    bounds.left = bounded.x;
    bounds.top = bounded.y;
  }

  useEffect(() => {
    const update = () => setView(viewport());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  useLayoutEffect(() => {
    if (open || !buttonRef.current) return;
    const element = buttonRef.current;
    const measure = () => setSize({ width: element.offsetWidth, height: element.offsetHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.focus({ preventScroll: true });
    else if (wasOpen.current) buttonRef.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);

  const finishDrag = (event) => {
    const drag = gesture.current;
    if (!drag || event.pointerId !== drag.id) return;
    suppressClick.current = drag.moved;
    gesture.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const finishPanelDrag = (event) => {
    if (panelGesture.current?.id !== event.pointerId) return;
    panelGesture.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return createPortal(<>
    {!open && <button
      ref={buttonRef}
      type="button"
      className="ocare-assistant-launcher ocare-draggable-launcher"
      style={{ left: anchor.x, top: anchor.y, right: "auto", bottom: "auto" }}
      aria-label={`Open oCarely for ${residentName}. Drag to move, or use arrow keys.`}
      aria-haspopup="dialog"
      title="Click to chat · Drag to move · Arrow keys to reposition"
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary) return;
        suppressClick.current = false;
        gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, anchor, moved: false };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const drag = gesture.current;
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved && Math.hypot(dx, dy) < 6) return;
        drag.moved = true;
        setPosition(boundLauncher({ x: drag.anchor.x + dx, y: drag.anchor.y + dy }, size, viewport()));
      }}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={() => { gesture.current = null; }}
      onClick={(event) => {
        if (event.detail !== 0 && suppressClick.current) { suppressClick.current = false; return; }
        setPanelPosition(null);
        onOpen();
      }}
      onKeyDown={(event) => {
        const delta = { ArrowLeft: [-24, 0], ArrowRight: [24, 0], ArrowUp: [0, -24], ArrowDown: [0, 24] }[event.key];
        if (!delta) return;
        event.preventDefault();
        setPosition(boundLauncher({ x: anchor.x + delta[0], y: anchor.y + delta[1] }, size, view));
      }}
    ><span className="ocare-assistant-launcher-icon ocarely-mark">{mark}</span><span>Ask Ova<small>Your resident virtual assistant</small></span></button>}
    {open && <section ref={panelRef} tabIndex={-1} role="dialog" aria-modal="false" aria-label="Ova resident virtual assistant"
      className="ocare-assistant-drawer ocare-floating-panel" style={bounds}
      onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); onClose(); } }}>
      <header className="ant-drawer-header"><div className="ocare-panel-drag-handle" role="button" tabIndex={0}
        aria-label="Move oCarely window. Drag or use arrow keys." title="Drag to move · Arrow keys to reposition"
        onPointerDown={(event) => {
          if (event.button !== 0 || !event.isPrimary) return;
          panelGesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, left: bounds.left, top: bounds.top };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const drag = panelGesture.current;
          if (!drag || drag.id !== event.pointerId) return;
          setPanelPosition(boundLauncher({ x: drag.left + event.clientX - drag.x, y: drag.top + event.clientY - drag.y }, bounds, viewport()));
        }}
        onPointerUp={finishPanelDrag} onPointerCancel={finishPanelDrag}
        onLostPointerCapture={() => { panelGesture.current = null; }}
        onKeyDown={(event) => {
          const delta = { ArrowLeft: [-24, 0], ArrowRight: [24, 0], ArrowUp: [0, -24], ArrowDown: [0, 24] }[event.key];
          if (!delta) return;
          event.preventDefault();
          setPanelPosition(boundLauncher({ x: bounds.left + delta[0], y: bounds.top + delta[1] }, bounds, view));
        }}
      >{title}</div><button type="button" className="ocare-floating-close" aria-label="Close oCarely" title="Close chat (Esc)" onClick={onClose}>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
      </button></header>
      <div className="ant-drawer-body">{children}</div>
    </section>}
  </>, document.body);
}
